'use client';

import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useAppSelector } from '@/store/hooks';
import { getUi, showModal } from '@/store/reducers/ui-slice';
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Box, Stack } from '@mui/system';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Eye } from '@phosphor-icons/react/dist/ssr/Eye';
import { EyeSlash } from '@phosphor-icons/react/dist/ssr/EyeSlash';
import { Selection } from '@phosphor-icons/react/dist/ssr/Selection';
import { DefaultValues, FieldValues } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { CustomConfigInputProps, InputType, ModalDrawer } from '@/types/general';
import { formatCurrencyCode } from '@/lib/helpers';

import HelpInfoPopover from './help-info-popover';
import { UploadCard } from './upload-card';

const TextEditorInput = dynamic(() => import('@/components/core/text-editor-input'), {
  ssr: false,
});

export function CustomConfigInputV2<TSchema extends FieldValues>(
  props: CustomConfigInputProps & {
    defaultData?: DefaultValues<TSchema>;
    setDefaultData?: React.Dispatch<React.SetStateAction<TSchema>>;
    onChange?: (value: string | string[] | boolean | number | number[] | undefined) => void;
  }
): React.JSX.Element | null {
  const { label, name, type, description, direction, hidden, required, size, dir } = props;

  if (hidden) return null;
  return (
    <Grid sx={{ width: 1 }} key={String(name)} size={size || { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
      <Box
        sx={{
          width: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          sx={{
            width: 1,
            alignItems: direction === 'row' ? 'center' : 'start',
            justifyContent: direction === 'row' ? 'space-between' : 'start',
            textAlign: dir === 'rtl' ? 'end' : 'start',
          }}
          direction={direction || 'column'}
        >
          <Box sx={{ display: 'flex', gap: 0.8 }}>
            {type !== InputType.EDITOR ? <InputLabel required={required}>{label}</InputLabel> : null}

            {!description ? null : <HelpInfoPopover content={description} />}
          </Box>
          {InputField(props)}
        </Stack>
      </Box>
    </Grid>
  );
}

export function InputField<TSchema>(
  props: CustomConfigInputProps & {
    defaultData?: DefaultValues<TSchema>;
    setDefaultData?: React.Dispatch<React.SetStateAction<TSchema>>;
    onChange?: (value: string | string[] | boolean | number | number[]) => void;
  }
): React.JSX.Element {
  const {
    label,
    name,
    type,
    placeholder,
    value,
    customKey,
    disabled,
    startAdornment,
    endAdornment,
    falseLabel,
    id,
    image,
    menuItems,
    multiline,
    numberProps,
    onChange,
    setDefaultData,
    recommendedDimensions,
    required,
    trueLabel,
    width,
    regex,
    inputProps,
    readOnly,
    error,
    isLoading,
    dir,
  } = props;

  const dispatch = useDispatch();
  const { selectedPageItems } = useAppSelector(getUi);
  const [showPassword, setShowPassword] = useState<boolean>();

  const updateNestedProperty = (obj: object, path: string, targetValue: unknown): Record<string, unknown> => {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const currentObj = JSON.parse(JSON.stringify(obj)); // Deep copy the object

    let tempObj: Record<string, unknown> = currentObj;

    for (const key of keys) {
      if (!tempObj[key] || typeof tempObj[key] !== 'object') {
        tempObj[key] = {};
      } else {
        tempObj[key] = { ...tempObj[key] };
      }
      tempObj = tempObj[key] as Record<string, unknown>;
    }

    tempObj[lastKey] = targetValue;

    return currentObj;
  };

  //   const updateNestedProperty = <T extends object>(obj: T, path: string, targetValue: unknown): void => {
  //     const keys = path.split('.');
  //     let current: Record<string, unknown> = obj as Record<string, unknown>;

  //     for (let i = 0; i < keys.length - 1; i++) {
  //       const key = keys[i];

  //       if (!(key in current) || typeof current[key] !== 'object') {
  //         current[key] = {};
  //       }

  //       current = current[key] as Record<string, unknown>;
  //     }

  //     current[keys[keys.length - 1]] = targetValue;
  //   };

  const handleChange = (fieldName: string, targetValue: string | string[] | boolean | number | number[]): void => {
    let finalValue: string | string[] | boolean | number | number[] = targetValue;

    if (
      type === InputType.NUMBER_ARRAY &&
      Array.isArray(targetValue) &&
      targetValue.some((v) => typeof v === 'string' && !isNaN(Number(v)))
    ) {
      finalValue = targetValue.map(Number);
    }

    // Convert strings to numbers if appropriate
    else if (type === InputType.NUMBER && typeof targetValue === 'string' && !isNaN(Number(targetValue))) {
      finalValue = Number(targetValue);
    }

    if (regex) {
      if (!regex.test(targetValue as string)) {
        return;
      }
    }

    if (type === InputType.INPUT && inputProps?.maxLength) {
      if (value && typeof value === 'string' && value.length > inputProps.maxLength) {
        return;
      }
    }

    setDefaultData?.((prevValue: TSchema): TSchema => {
      if (!prevValue) return prevValue; // Check if prevValue is valid
      const updatedValue = updateNestedProperty(prevValue as object, fieldName, finalValue);
      return updatedValue as TSchema;
    });

    onChange?.(finalValue);
  };

  useEffect(() => {
    if (selectedPageItems?.target === name && selectedPageItems?.index === id) {
      onChange?.(`${selectedPageItems.slug}`);
    }
  }, [selectedPageItems]);

  switch (type) {
    case InputType.SELECT:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <Select
            sx={{ width: width || '100%', maxWidth: '100%' }}
            value={value}
            name={name}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value as string);
            }}
            MenuProps={{
              sx: {
                zIndex: 1702,
              },
              slotProps: {
                paper: {
                  style: {
                    maxHeight: 200,
                  },
                },
              },
            }}
          >
            {menuItems?.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );

    case InputType.AUTO_COMPLETE:
      if (!menuItems) return <></>;
      return (
        <Autocomplete
          disablePortal
          options={menuItems}
          onChange={(e, newVal) => {
            if (newVal) {
              handleChange(name, newVal.value);
            }
          }}
          sx={{ width: width || '100%', maxWidth: '100%' }}
          renderInput={(params) => <TextField {...params} />}
        />
      );
    case InputType.MULTISELECT:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <Select
            defaultValue={value || []}
            value={value}
            name={name}
            disabled={disabled}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value as string[]);
            }}
            multiple
            sx={{ width: width || '100%', maxWidth: '100%' }}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.isArray(selected) && selected?.length
                  ? selected?.map((selectedValue: string) => {
                      const item = menuItems?.find((item) => item.value === selectedValue);
                      const itemLabel = item ? item.label : selectedValue;

                      return (
                        <Chip
                          key={selectedValue}
                          label={itemLabel}
                          color="default"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onDelete={(e) => {
                            e.stopPropagation();
                            // onChange?.(selected.filter((item: string) => item !== value));
                            handleChange(
                              name,
                              selected.filter((selectedItem: string) => selectedItem !== selectedValue)
                            );
                          }}
                        />
                      );
                    })
                  : null}
              </Box>
            )}
            MenuProps={{
              sx: {
                zIndex: 1702,
              },
              slotProps: {
                paper: {
                  style: {
                    maxHeight: 200,
                  },
                },
              },
            }}
          >
            {isLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ marginRight: 1 }} />
                Loading...
              </MenuItem>
            ) : (
              menuItems?.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      );

    case InputType.INPUT:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <OutlinedInput
            value={value}
            placeholder={placeholder}
            name={name}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value);
            }}
            dir={dir}
            multiline={multiline}
            sx={{ width: width || '100%', maxWidth: '100%', direction: dir }}
            endAdornment={endAdornment}
            startAdornment={startAdornment}
            disabled={disabled}
            required={required}
            readOnly={readOnly}
            autoComplete="off"
          />
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );

    case InputType.PASSWORD:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <OutlinedInput
            value={value}
            placeholder={placeholder}
            name={name}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value);
            }}
            sx={{ width: width || '100%', maxWidth: '100%' }}
            disabled={disabled}
            required={required}
            readOnly={readOnly}
            type={showPassword ? 'text' : 'password'}
            endAdornment={
              showPassword ? (
                <Eye
                  cursor="pointer"
                  fontSize="var(--icon-fontSize-md)"
                  onClick={(): void => {
                    setShowPassword(false);
                  }}
                />
              ) : (
                <EyeSlash
                  cursor="pointer"
                  fontSize="var(--icon-fontSize-md)"
                  onClick={(): void => {
                    setShowPassword(true);
                  }}
                />
              )
            }
            autoComplete="new-password"
          />
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );

    case InputType.PAGE_PICKER:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <OutlinedInput
            value={value}
            name={name}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value);
            }}
            multiline={multiline}
            sx={{ width: width || '100%', maxWidth: '100%' }}
            endAdornment={
              <InputAdornment position="end">
                <Button
                  variant="text"
                  size="small"
                  sx={{ display: 'flex !important' }}
                  onClick={() => {
                    dispatch(
                      showModal({
                        open: ModalDrawer.PAGE_PICKER,
                        content: {
                          target: name,
                        },
                      })
                    );
                  }}
                  color="success"
                  startIcon={<Selection />}
                >
                  Pick Page
                </Button>
              </InputAdornment>
            }
            disabled={disabled}
            required={required}
          />
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );
    case InputType.NUMBER:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <OutlinedInput
            value={value}
            name={name}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value);
            }}
            type="number"
            sx={{ width: width || '100%', maxWidth: '100%' }}
            disabled={disabled}
            required={required}
            endAdornment={
              numberProps?.currency ? (
                <Typography>
                  {numberProps.currency === 'USD' ? '$' : formatCurrencyCode(numberProps.currency)}
                </Typography>
              ) : numberProps?.isPercentage ? (
                <Typography>%</Typography>
              ) : null
            }
          />
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );
    // case InputType.PASSWORD:
    //   return <PasswordInput field={props} width={width} required={required} />;

    case InputType.MULTILINE_INPUT:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <OutlinedInput
            multiline
            disabled={disabled}
            value={value}
            name={name}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value);
            }}
            sx={{ width: width || '100%', maxWidth: '100%' }}
          />
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );
    case InputType.DATE_PICKER:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <LocalizationProvider {...props} dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{ width: width || '100%', maxWidth: '100%' }}
              slotProps={{
                textField: {
                  size: 'small',
                },
                popper: {
                  sx: { zIndex: 1500 },
                },
              }}
            />
          </LocalizationProvider>
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );
    case InputType.DATE_TIME_PICKER:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <LocalizationProvider {...props} dateAdapter={AdapterDayjs}>
            <DateTimePicker
              sx={{ width: width || '100%', maxWidth: '100%' }}
              slotProps={{
                textField: {
                  size: 'small',
                },
                popper: {
                  sx: { zIndex: 1500 },
                },
              }}
            />
          </LocalizationProvider>
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );

    case InputType.BOOLEAN:
      return (
        <FormControlLabel
          control={
            <Switch
              value={value}
              name={name}
              onChange={(e) => {
                handleChange(e.target.name, e.target.value);
              }}
              defaultChecked={value as boolean}
              disabled={disabled}
            />
          }
          label={trueLabel || falseLabel ? (value ? trueLabel : falseLabel) : undefined}
        />
      );

    case InputType.CHECKBOX:
      return (
        <FormControlLabel
          control={
            <Checkbox
              name={name}
              onChange={(e) => {
                handleChange(e.target.name, e.target.checked);
              }}
              defaultChecked={value as boolean}
              disabled={disabled}
              checked={value as boolean}
            />
          }
          label={trueLabel || falseLabel ? (value ? trueLabel : falseLabel) : undefined}
        />
      );

    case InputType.IMAGE:
      return (
        <UploadCard
          id={id || 1}
          width={width}
          customKey={customKey}
          recommendedDimensions={recommendedDimensions}
          image={image}
          onUpload={() => {
            // empty block
          }}
        />
      );
    case InputType.EDITOR:
      return (
        <TextEditorInput
          initialContent={value as string}
          label={label}
          required={required}
          name={name}
          onChange={(e) => {
            onChange?.(e.value);
          }}
        />
      );
    default:
      return (
        <FormControl error={Boolean(error)} fullWidth disabled={disabled}>
          <OutlinedInput
            value={value}
            name={name}
            onChange={(e) => {
              handleChange(e.target.name, e.target.value);
            }}
            sx={{ width: width || '100%', maxWidth: '100%' }}
          />
          {props.error ? <FormHelperText>{props.error}</FormHelperText> : null}
          {props.herlperText ? <FormHelperText>{props.herlperText}</FormHelperText> : null}
        </FormControl>
      );
  }
}
